
// src/registerSW.js
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
    return reg;
  } catch (e) {
    console.warn('SW registration failed', e);
    return null;
  }
}

/**
 * Check if specific assets are already cached
 * @param {string|string[]} assetsUrl - URL(s) of asset lists to check
 * @returns {Promise<{cached: boolean, count: number}>}
 */
export async function checkOfflineCache(assetsUrl) {
  if (!('caches' in window)) {
    return { cached: false, count: 0 };
  }

  try {
    // Support both single URL and array of URLs
    const assetUrls = Array.isArray(assetsUrl) ? assetsUrl : [assetsUrl];

    // Fetch all asset lists
    const allAssets = [];
    for (const url of assetUrls) {
      try {
        const response = await fetch(url);
        const assets = await response.json();
        allAssets.push(...assets);
      } catch (e) {
        console.warn(`Failed to fetch asset list: ${url}`, e);
        return { cached: false, count: 0 };
      }
    }

    // Check if all assets are cached
    const cacheNames = await caches.keys();
    const runtimeCache = cacheNames.find(name => name.includes('runtime'));
    const coreCache = cacheNames.find(name => name.includes('core'));

    if (!runtimeCache && !coreCache) {
      return { cached: false, count: 0 };
    }

    // Check both caches for assets
    let cachedCount = 0;
    for (const asset of allAssets) {
      let found = false;

      if (runtimeCache) {
        const cache = await caches.open(runtimeCache);
        const match = await cache.match(asset);
        if (match) found = true;
      }

      if (!found && coreCache) {
        const cache = await caches.open(coreCache);
        const match = await cache.match(asset);
        if (match) found = true;
      }

      if (found) cachedCount++;
    }

    // Consider cached if at least 90% of assets are present
    const cached = cachedCount >= allAssets.length * 0.9;
    return { cached, count: cachedCount };
  } catch (error) {
    console.error('Error checking cache:', error);
    return { cached: false, count: 0 };
  }
}

export async function requestOfflineCache(assetsUrl = '/offline-assets.json', onProgress) {
  if (!('serviceWorker' in navigator)) {
    alert('Offline not supported on this browser');
    return;
  }

  // Support both single URL and array of URLs
  const assetUrls = Array.isArray(assetsUrl) ? assetsUrl : [assetsUrl];

  const reg = await navigator.serviceWorker.getRegistration();
  const sw = navigator.serviceWorker.controller || reg?.active;
  if (!sw) {
    alert('Preparing offline… please try again in a few seconds.');
    // Attempt to register quickly
    await registerServiceWorker();
    setTimeout(() => requestOfflineCache(assetsUrl, onProgress), 1000);
    return;
  }

  // Download each asset list sequentially
  let totalCached = 0;
  for (const url of assetUrls) {
    const result = await new Promise((resolve) => {
      const onMsg = (e) => {
        const { type, ok, count, error, current, total } = e.data || {};

        // Report progress updates
        if (type === 'CACHE_PROGRESS' && onProgress) {
          onProgress({ current, total });
        }

        if (type === 'CACHE_DONE') {
          navigator.serviceWorker.removeEventListener('message', onMsg);
          resolve({ ok, count, error });
        }
      };
      navigator.serviceWorker.addEventListener('message', onMsg);
      sw.postMessage({ type: 'CACHE_OFFLINE', assets: url });
    });

    if (!result.ok) {
      return result; // Return error immediately
    }
    totalCached += result.count || 0;
  }

  return { ok: true, count: totalCached };
}
